import h from 'hyperscript';
import brokenAxisMarker from '@disy/cadenza-icons/broken-axis-marker.svg';

import { icon } from 'cadenza/utils/icon/icon';
import { assertNonNullable } from 'cadenza/utils/custom-error';

import './animation-visualization.css';
import i18n from './animation-visualization.properties';

const COMPONENT_NAME = 'd-animation-visualization';

const NUMBER_AND_UNIT_REGEXP = /\s*(\d+)(\w+)/;

/**
 * Minimum width in px for non-overlapping area, incomplete frames and overset.
 * Those elements must have the same minimum width, otherwise the layout can break.
 */
const VISUALIZATION_ELEMENT_MIN_WIDTH = 28;

export class AnimationVisualization extends HTMLElement {

  /** Number of animation frames */
  _numberOfFrames: number;

  /** Label for complete animation frames or array of complete animation frame labels */
  _frameLabels: string | string[];

  /* Overlap between subsequent frames, in the step unit */
  _overlap: number;

  _windowSize: number;

  /** Label for the part of the data that cannot be animated because of the frame limit */
  _undersetLabel?: string;

  _overset?: OversetInfo;

  _rangePicker?: HTMLElement;

  constructor ({
    numberOfFrames,
    frameLabels,
    windowSize = 1,
    overlap = 0,
    undersetLabel,
    overset,
    rangePicker
  }: {
    numberOfFrames: number;
    frameLabels: string | string[];
    windowSize?: number;
    overlap?: number;
    undersetLabel?: string;
    overset?: OversetInfo;
    rangePicker?: HTMLElement;
  }) {
    super();
    this._numberOfFrames = numberOfFrames;
    this._frameLabels = frameLabels;
    this._windowSize = windowSize;
    this._overlap = overlap;
    this._undersetLabel = undersetLabel;
    this._overset = overset;
    this._rangePicker = rangePicker;
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this._render();
  }

  updateFrameLabels (frameLabels: string[], numberOfFrames: number, undersetLabel: string) {
    this._frameLabels = frameLabels;
    this._numberOfFrames = numberOfFrames;
    this._undersetLabel = undersetLabel;
    this._render();
  }

  _render () {
    this.innerHTML = '';

    if (this._numberOfFrames <= 1) {
      return;
    }

    const grid = h('.d-animation-visualization--frame-grid');
    if (this._overlap) {
      if (this._numberOfFrames <= 2) {
        grid.classList.add('overlap-no-gap-less-than-3-frames');
      } else {
        grid.classList.add('overlap');
      }
    }

    this._appendRangePicker(grid);
    this._appendHeadersAndFrames(grid);
    this._appendGapElements(grid);
    this._appendOverlapElements(grid);
    this._appendUnderset(grid);
    this._appendOverset(grid);

    this.append(grid);
  }

  _appendRangePicker (grid: HTMLElement) {
    if (this._rangePicker) {
      const startEndLabelContainer = h('.d-animation-visualization--start-end-labels',
        h('.d-animation-visualization--start', i18n('start')),
        h('.d-animation-visualization--end', i18n('end')));
      grid.append(startEndLabelContainer);
      this._rangePicker.classList.add('d-animation-visualization--range-picker');
      grid.append(this._rangePicker);
    }
  }

  _appendHeadersAndFrames (grid: HTMLElement) {
    const incompleteFrameIndex = this._overset ? this._numberOfFrames : undefined;

    grid.append(this._createFirstFrameNumber());
    grid.append(this._createFirstFrame(incompleteFrameIndex === 1));
    if (this._numberOfFrames >= 2) {
      grid.append(this._createSecondFrameNumber());
      grid.append(this._createSecondFrame(incompleteFrameIndex === 2));
    }
    if (this._numberOfFrames === 3) {
      grid.append(this._createLastFrameNumber(3));
      grid.append(this._createLastFrame(incompleteFrameIndex === 3));
    } else if (this._numberOfFrames >= 4) {
      grid.append(this._createSecondLastFrameNumber(this._numberOfFrames - 1));
      grid.append(this._createSecondLastFrame());
      grid.append(this._createLastFrameNumber(this._numberOfFrames));
      grid.append(this._createLastFrame(incompleteFrameIndex === this._numberOfFrames));
    }
  }

  _createFirstFrameNumber () {
    if (this._overlap) {
      return createFrameNumber(1,
        'd-animation-visualization--first-frame-to-second-frame-row2-start');
    } else {
      return createFrameNumber(1, 'd-animation-visualization--first-frame');
    }
  }

  _createSecondFrameNumber () {
    let additionalCssClass;
    if (this._overlap) {
      additionalCssClass = 'd-animation-visualization--second-frame-row2';
    } else if (this._numberOfFrames === 2) {
      additionalCssClass = 'd-animation-visualization--second-and-last-frame';
    } else {
      additionalCssClass = 'd-animation-visualization--second-frame';
    }
    return createFrameNumber(2, additionalCssClass);
  }

  _createSecondLastFrameNumber (frameNumber: number) {
    if (this._overlap) {
      return createFrameNumber(frameNumber,
        'd-animation-visualization--second-last-frame-number');
    } else {
      return createFrameNumber(frameNumber,
        'd-animation-visualization--second-last-frame');
    }
  }

  _createLastFrameNumber (frameNumber: number) {
    let cssClass;
    if (this._overlap && this._hasEvenNumberOfFrames()) {
      cssClass = 'd-animation-visualization--last-frame-num-row2';
    } else {
      cssClass = 'd-animation-visualization--last-frame-num-row1';
    }
    return createFrameNumber(frameNumber, cssClass);
  }

  _createCompleteFrame (additionalCssClass: string, frameIndex: number) {
    return h(`.d-animation-visualization--complete-frame.${additionalCssClass}`,
      h('p.ellipsis', typeof this._frameLabels === 'string' ? this._frameLabels : this._frameLabels[frameIndex]));
  }

  _createIncompleteFrame (additionalCssClass: string) {
    // if there is an incomplete frame we know there is an overset object
    assertNonNullable(this._overset);
    const incompleteFrame: HTMLElement = h(`.d-animation-visualization--incomplete-frame.${additionalCssClass}`,
      h('p.ellipsis', this._overset.incompleteFrameLabel));
    incompleteFrame.style.width = this._incompleteFrameCssWidth();
    return incompleteFrame;
  }

  _createFirstFrame (isIncomplete: boolean) {
    const positioningCssClass =
        'd-animation-visualization--first-frame.d-animation-visualization--frame-row1';
    if (isIncomplete) {
      return this._createIncompleteFrame(positioningCssClass);
    } else {
      return this._createCompleteFrame(
        positioningCssClass,
        this._getFrameIndex(0));
    }
  }

  _createSecondFrame (isIncomplete: boolean) {
    let positioningCssClass;

    if (this._overlap) {
      positioningCssClass =
          'd-animation-visualization--second-frame-row2.d-animation-visualization--frame-row2';
    } else {
      positioningCssClass =
          'd-animation-visualization--second-frame.d-animation-visualization--frame-row1';
    }

    if (isIncomplete) {
      return this._createIncompleteFrame(positioningCssClass);
    } else {
      return this._createCompleteFrame(positioningCssClass, this._getFrameIndex(1));
    }
  }

  _appendGapElements (grid: HTMLElement) {
    if (this._numberOfFrames <= 2) {
      return;
    }

    if (this._overlap) {
      grid.append(
        createGap('d-animation-visualization--gap',
          this._hasEvenNumberOfFrames()
            ? 'd-animation-visualization--gap-multi-row-row1-even-num'
            : 'd-animation-visualization--gap-multi-row-row1-odd-num'),
        createGap('d-animation-visualization--gap',
          'd-animation-visualization--gap-multi-row-row2'));
    } else {
      grid.append(createGap('d-animation-visualization--gap',
        'd-animation-visualization--gap-single-row'));
    }
  }

  _createSecondLastFrame () {
    let positioningCssClass;
    if (this._overlap) {
      positioningCssClass = this._hasOddNumberOfFrames()
        ? 'd-animation-visualization--frame-row2.d-animation-visualization--second-last-frame'
        : 'd-animation-visualization--frame-row1.d-animation-visualization--second-last-frame';
    } else {
      positioningCssClass =
          'd-animation-visualization--frame-row1.d-animation-visualization--second-last-frame';
    }
    return this._createCompleteFrame(positioningCssClass, this._getFrameIndex(-2));
  }

  /**
   * Creates the element for the last frame (to be used if there are more than 2 frames).
   *
   * @param isIncomplete
   * @return HTML element for the last frame
   */
  _createLastFrame (isIncomplete: boolean) {
    let positioningCssClass;
    if (this._overlap) {
      positioningCssClass = this._hasOddNumberOfFrames()
        ? 'd-animation-visualization--last-frame-row1'
        : 'd-animation-visualization--last-frame-row2';
    } else {
      positioningCssClass = 'd-animation-visualization--last-frame-row1';
    }

    if (isIncomplete) {
      return this._createIncompleteFrame(positioningCssClass);
    } else {
      return this._createCompleteFrame(
        positioningCssClass,
        this._getFrameIndex(-1));
    }
  }

  _appendOverlapElements (grid: HTMLElement) {
    if (!this._overlap) {
      return;
    }

    grid.append(this._createNonOverlappingPartMarker());
    if (this._numberOfFrames > 2) {
      this._appendOverlapGapFillers(grid);
    }
  }

  _nonOverlappingPart () {
    return this._windowSize - this._overlap;
  }

  _nonOverlappingCssWidth () {
    const nonOverlappingPartToWindowSizeRatio = this._nonOverlappingPart() / this._windowSize;
    return fractionOfCompleteFrameCssSize(
      nonOverlappingPartToWindowSizeRatio, VISUALIZATION_ELEMENT_MIN_WIDTH);
  }

  _overlappingCssWidth () {
    const overlappingPartToWindowSizeRatio = this._overlap / this._windowSize;
    return fractionOfCompleteFrameCssSize(
      overlappingPartToWindowSizeRatio, VISUALIZATION_ELEMENT_MIN_WIDTH);
  }

  _incompleteFrameCssWidth () {
    // we only call this method if we have an incomplete frame and thus an overset
    assertNonNullable(this._overset);
    const incompleteFrameToCompleteFrameSizeRatio = 1 - this._overset.oversetToCompleteFrameRatio;
    return fractionOfCompleteFrameCssSize(
      incompleteFrameToCompleteFrameSizeRatio, VISUALIZATION_ELEMENT_MIN_WIDTH);
  }

  _oversetCssWidth () {
    // we only call this method if we have an incomplete frame and thus an overset
    assertNonNullable(this._overset);
    return fractionOfCompleteFrameCssSize(
      this._overset.oversetToCompleteFrameRatio, VISUALIZATION_ELEMENT_MIN_WIDTH);
  }

  _createNonOverlappingPartMarker () {
    const nonOverlappingPartMarker = h('.d-animation-visualization-overlap',
      h('.d-animation-visualization--overlap-text-box.ellipsis',
        this._nonOverlappingPart().toString()),
      h('.d-animation-visualization--overlap-triangle'));
    nonOverlappingPartMarker.style.width = this._nonOverlappingCssWidth();
    return nonOverlappingPartMarker;
  }

  _appendOverlapGapFillers (grid: HTMLElement) {
    this._appendFirstRowOverlapGapFillers(grid);
    this._appendSecondRowOverlapGapFillers(grid);
    this._appendLineEndFiller(grid, this._hasEvenNumberOfFrames() ? 1 : 2);
  }

  _appendFirstRowOverlapGapFillers (grid: HTMLElement) {
    const leftFiller = h('.d-animation-visualization-left-overlap-gap-filler'
      + '.d-animation-visualization--frame-row1');
    leftFiller.style.width = this._nonOverlappingCssWidth();
    grid.append(leftFiller);

    if (this._hasOddNumberOfFrames()) {
      const rightFiller = h('.d-animation-visualization-right-overlap-gap-filler'
          + '.d-animation-visualization--frame-row1');
      if (this._hasOddNumberOfFrames()) {
        rightFiller.style.width = this._nonOverlappingCssWidth();
      } else {
        rightFiller.style.width = this._overlappingCssWidth();
      }
      grid.append(rightFiller);
    }
  }

  _appendSecondRowOverlapGapFillers (grid: HTMLElement) {
    if (this._hasEvenNumberOfFrames()) {
      const rightFiller = h('.d-animation-visualization-right-overlap-gap-filler'
          + '.d-animation-visualization--frame-row2');
      if (this._overset) {
        rightFiller.style.width = this._oversetCssWidth();
      } else {
        rightFiller.style.width = this._nonOverlappingCssWidth();
      }
      grid.append(rightFiller);
    }
  }

  _appendLineEndFiller (grid: HTMLElement, row: number) {
    grid.append(h('.d-animation-visualization--line-end-filler'
        + `.d-animation-visualization--frame-row${row}`));
  }

  _appendUnderset (grid: HTMLElement) {
    if (this._undersetLabel) {
      const positioningClass = this._overlap && this._hasEvenNumberOfFrames()
        ? 'd-animation-visualization--frame-row2'
        : 'd-animation-visualization--frame-row1';
      grid.append(
        h(`.d-animation-visualization--underset.${positioningClass}`, this._undersetLabel));
    }
  }

  _appendOverset (grid: HTMLElement) {
    if (this._overset) {
      const positioningClass = this._overlap && this._hasEvenNumberOfFrames()
        ? 'd-animation-visualization--overset-row2'
        : 'd-animation-visualization--overset-row1';
      const oversetElement =
          h(`.d-animation-visualization--overset.${positioningClass}`,
            h('.ellipsis', this._overset.oversetLabel));
      oversetElement.style.width = this._oversetCssWidth();
      grid.append(oversetElement);
    }
  }

  _getFrameIndex (relativeIndex: number) {
    if (typeof this._frameLabels === 'string') {
      return 0;
    }
    return  relativeIndex >= 0 ? relativeIndex : this._frameLabels.length + relativeIndex;
  }

  _hasOddNumberOfFrames () {
    return this._numberOfFrames % 2 === 1;
  }

  _hasEvenNumberOfFrames () {
    return !this._hasOddNumberOfFrames();
  }

}

export interface OversetInfo {
  /*
   * Label for the last frame if it is smaller than the configured window size because otherwise it
   * would extend beyond the animation end date.
   */
  incompleteFrameLabel: string;

  /**
   * Label for the "overset" (part that is cut off the last frame because it is after the animation
   * end date)
   */
  oversetLabel: string;

  oversetToCompleteFrameRatio: number;

  lastFramesEndDistToCompleteFrameRatio: number;
}

customElements.define(COMPONENT_NAME, AnimationVisualization);

function createFrameNumber (frameNumber: number, additionalCssClass: string) {
  return h(`.d-animation-visualization--frame-number.${additionalCssClass}`, frameNumber.toString());
}

/**
 * Create the visualization for representing the not displayed frames when the animation has more
 * than 5 frames.
 *
 * @param cssClasses - CSS classes for positioning the gap in the grid
 * @return visualization of the frames left out in the animation visualization
 */
function createGap (...cssClasses: string[]): HTMLElement {
  // By having the text (later the icon) nested and with a width filling the whole screen, we can
  // make the grid column for the gap expand only if there is content in it (in combination with
  // "overflow: hidden on the parent element and "...gap-start] fit-content(100%) [gap-end..."
  // in the grid-template-columns of the CSS grid layout.
  return h(
    `.d-animation-visualization--gap.${cssClasses.join('.')}`,
    h('p.d-animation-visualization--gap-icons',
      icon(brokenAxisMarker, { styleClass: 'd-animation-visualization--broken-axis-marker' }),
      icon(brokenAxisMarker, { styleClass: 'd-animation-visualization--broken-axis-marker' })
    )
  );
}

function fractionOfCompleteFrameCssSize (fraction: number, minSizeInPx: number): string {
  const completeFrameWidth =
      getComputedStyle(document.documentElement).getPropertyValue('--complete-frame-width');
  const maxSize = `calc(${completeFrameWidth} - ${minSizeInPx}px)`;
  const completeFrameWidthNumber = Number(completeFrameWidth.match(NUMBER_AND_UNIT_REGEXP)![1]);
  const unit = completeFrameWidth.match(NUMBER_AND_UNIT_REGEXP)![2];
  const completeFrameWidthSmallScreenFallback = getComputedStyle(document.documentElement)
    .getPropertyValue('--complete-frame-width-small-screen-fallback');
  const completeFrameWidthSmallScreenFallbackNumber =
      Number(completeFrameWidthSmallScreenFallback.match(NUMBER_AND_UNIT_REGEXP)![1]);
  const fallbackUnit = completeFrameWidthSmallScreenFallback.match(NUMBER_AND_UNIT_REGEXP)![2];
  const overlapWidth = completeFrameWidthNumber * fraction;
  const overlapWidthSmallScreenFallback = completeFrameWidthSmallScreenFallbackNumber * fraction;
  // The non-overlapping part gets a width with the correct relation to the complete frame size,
  // except for very small non-overlapping parts. Those get displayed with a fixed minimum width
  // because otherwise even one-digit labels would not be readable anymore.
  return `min(${maxSize}, max(${minSizeInPx}px,`
   + ` min(${overlapWidth}${unit}, ${overlapWidthSmallScreenFallback}${fallbackUnit})))`;
}
